import React, { useState, useEffect } from 'react';
import {
  portalDataApi,
  formatTimeRange,
  durationMinutes,
  type PortalDeclaration,
  type PortalReservation,
  type AvailabilityMode,
  type CancelReason,
} from './PortalApiClient';

interface Props {
  isoDate: string;            // YYYY-MM-DD
  dateLabel: string;          // human readable, e.g. "Wed 3 Jun"
  existing: PortalDeclaration | null;
  reservationsForDay: PortalReservation[]; // shifts on this specific date
  onClose: () => void;
  onChanged: () => void;       // called after successful declare/cancel
}

type View =
  | { kind: 'mode-picker' }
  | { kind: 'shift-list' }
  | { kind: 'custom-form' }
  | { kind: 'unavailable-form' }
  | { kind: 'cancel-form' };

export function PortalDeclareSheet({
  isoDate,
  dateLabel,
  existing,
  reservationsForDay,
  onClose,
  onChanged,
}: Props) {
  const [view, setView] = useState<View>({ kind: 'mode-picker' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Custom-hours form state
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  // Unavailable form
  const [unavailableReason, setUnavailableReason] = useState('');
  // Cancel form
  const [cancelReason, setCancelReason] = useState<CancelReason | null>(null);
  const [cancelNotes, setCancelNotes] = useState('');

  // Lock body scroll while sheet open
  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = orig; };
  }, []);

  async function submitDeclare(body: Parameters<typeof portalDataApi.declare>[0]) {
    setSubmitting(true);
    setError(null);
    try {
      await portalDataApi.declare(body);
      onChanged();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function submitCancel() {
    if (!existing) return;
    if (!cancelReason) { setError('Choose a reason.'); return; }
    if (cancelReason === 'OTHER' && !cancelNotes.trim()) {
      setError('Tell us why.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await portalDataApi.cancel(existing.id, cancelReason, cancelNotes.trim() || undefined);
      onChanged();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  function pickMode(mode: AvailabilityMode) {
    setError(null);
    if (mode === 'RESERVED') setView({ kind: 'shift-list' });
    else if (mode === 'CUSTOM') setView({ kind: 'custom-form' });
    else if (mode === 'UNAVAILABLE') setView({ kind: 'unavailable-form' });
    else submitDeclare({ date: isoDate, mode: 'ON_CALL' });
  }

  return (
    <div className="portal-sheet-backdrop" onClick={onClose}>
      <div className="portal-sheet" onClick={e => e.stopPropagation()}>
        <div className="portal-sheet-handle" />
        <div className="portal-sheet-header">
          <div>
            <div className="portal-sheet-title">{dateLabel}</div>
            {existing && (
              <div className="portal-sheet-subtitle">
                {renderExistingSummary(existing)}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="portal-sheet-close"
            aria-label="Close"
          >×</button>
        </div>

        {error && <div className="portal-error">{error}</div>}

        <div className="portal-sheet-body">
          {view.kind === 'mode-picker' && (
            <ModePicker
              onPick={pickMode}
              hasExisting={!!existing}
              onCancelExisting={() => setView({ kind: 'cancel-form' })}
              disabled={submitting}
            />
          )}

          {view.kind === 'shift-list' && (
            <ReservationList
              shifts={reservationsForDay}
              disabled={submitting}
              onBack={() => setView({ kind: 'mode-picker' })}
              onPick={reservationId => submitDeclare({ date: isoDate, mode: 'RESERVED', reservationId })}
            />
          )}

          {view.kind === 'custom-form' && (
            <CustomForm
              customStart={customStart}
              customEnd={customEnd}
              setCustomStart={setCustomStart}
              setCustomEnd={setCustomEnd}
              disabled={submitting}
              onBack={() => setView({ kind: 'mode-picker' })}
              onSubmit={() => {
                if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(customStart)
                 || !/^([01]\d|2[0-3]):[0-5]\d$/.test(customEnd)) {
                  setError('Enter both times in HH:MM format.');
                  return;
                }
                if (customStart === customEnd) {
                  setError('Start and end cannot be the same.');
                  return;
                }
                const mins = durationMinutes(customStart, customEnd);
                const hours = Math.round(mins / 60 * 10) / 10;
                if (mins > 13 * 60) {
                  const ok = window.confirm(
                    `This stay is ${hours} hours long. Is that right?`
                  );
                  if (!ok) return;
                }
                submitDeclare({ date: isoDate, mode: 'CUSTOM', customStart, customEnd });
              }}
            />
          )}

          {view.kind === 'unavailable-form' && (
            <UnavailableForm
              reason={unavailableReason}
              setReason={setUnavailableReason}
              disabled={submitting}
              onBack={() => setView({ kind: 'mode-picker' })}
              onSubmit={() => submitDeclare({
                date: isoDate,
                mode: 'UNAVAILABLE',
                unavailableReason: unavailableReason.trim() || undefined,
              })}
            />
          )}

          {view.kind === 'cancel-form' && (
            <CancelForm
              cancelReason={cancelReason}
              setCancelReason={setCancelReason}
              cancelNotes={cancelNotes}
              setCancelNotes={setCancelNotes}
              disabled={submitting}
              onBack={() => setView({ kind: 'mode-picker' })}
              onSubmit={submitCancel}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function renderExistingSummary(d: PortalDeclaration): string {
  if (d.status === 'CANCELLED') return 'Currently cancelled';
  if (d.mode === 'RESERVED' && d.reservation) {
    const tail = d.status === 'PENDING_OVERFLOW' ? ' (pending review)' : '';
    return `Currently: stay ${formatTimeRange(d.reservation.startTime, d.reservation.endTime)}${tail}`;
  }
  if (d.mode === 'CUSTOM' && d.customStart && d.customEnd) {
    const tail = d.status === 'PENDING_APPROVAL' ? ' (pending approval)' : '';
    return `Currently: custom ${formatTimeRange(d.customStart, d.customEnd)}${tail}`;
  }
  if (d.mode === 'ON_CALL') return 'Currently: on call';
  if (d.mode === 'UNAVAILABLE') return 'Currently: unavailable';
  return '';
}

// ----- Sub-views -----

interface ModePickerProps {
  onPick: (mode: AvailabilityMode) => void;
  hasExisting: boolean;
  onCancelExisting: () => void;
  disabled: boolean;
}
function ModePicker({ onPick, hasExisting, onCancelExisting, disabled }: ModePickerProps) {
  return (
    <>
      <ModeCard
        icon="🕐"
        title="Reservation"
        subtitle="Pick from published stays"
        onClick={() => onPick('RESERVED')}
        disabled={disabled}
      />
      <ModeCard
        icon="⏱"
        title="Custom hours"
        subtitle="Propose your own times (needs approval)"
        onClick={() => onPick('CUSTOM')}
        disabled={disabled}
      />
      <ModeCard
        icon="📞"
        title="On call"
        subtitle="Available if needed"
        onClick={() => onPick('ON_CALL')}
        disabled={disabled}
      />
      <ModeCard
        icon="⛔"
        title="Unavailable"
        subtitle="Not working this day"
        onClick={() => onPick('UNAVAILABLE')}
        disabled={disabled}
      />
      {hasExisting && (
        <button
          type="button"
          onClick={onCancelExisting}
          className="portal-cancel-decl-button"
          disabled={disabled}
        >
          Cancel this declaration
        </button>
      )}
    </>
  );
}

interface ModeCardProps {
  icon: string;
  title: string;
  subtitle: string;
  onClick: () => void;
  disabled: boolean;
}
function ModeCard({ icon, title, subtitle, onClick, disabled }: ModeCardProps) {
  return (
    <button
      type="button"
      className="portal-mode-card"
      onClick={onClick}
      disabled={disabled}
    >
      <span className="portal-mode-icon">{icon}</span>
      <span className="portal-mode-text">
        <span className="portal-mode-title">{title}</span>
        <span className="portal-mode-subtitle">{subtitle}</span>
      </span>
    </button>
  );
}

interface ShiftListProps {
  shifts: PortalReservation[];
  disabled: boolean;
  onBack: () => void;
  onPick: (reservationId: string) => void;
}
function ReservationList({ shifts, disabled, onBack, onPick }: ShiftListProps) {
  return (
    <>
      <SectionHeader title="Pick a stay" onBack={onBack} />
      {shifts.length === 0 && (
        <div className="portal-empty">
          No stays published for this day yet.<br />
          Try Custom hours, On call, or Unavailable.
        </div>
      )}
      {/* Identical spots arrive collapsed, so she sees "4 available" rather
          than four indistinguishable rows. Picking one claims a specific spot;
          `id` is whichever is free first. */}
      {shifts.map((s, i) => (
        <button
          key={s.id ?? `full-${s.startTime}-${s.endTime}-${i}`}
          type="button"
          className="portal-shift-row"
          onClick={() => s.id && onPick(s.id)}
          disabled={disabled || s.isFull}
        >
          <span className="portal-shift-time">
            {formatTimeRange(s.startTime, s.endTime)}
          </span>
          <span className={'portal-shift-cap' + (s.isFull ? ' is-full' : '')}>
            {s.isFull
              ? 'all taken'
              : `${s.availableCount} available`}
          </span>
        </button>
      ))}
      {shifts.some(s => s.isFull) && (
        <div className="portal-hint" style={{ marginTop: 8 }}>
          Times showing as taken are already full. Declare Custom hours or On call
          instead and staff will find you a place if one opens.
        </div>
      )}
    </>
  );
}

interface CustomFormProps {
  customStart: string;
  customEnd: string;
  setCustomStart: (s: string) => void;
  setCustomEnd: (s: string) => void;
  disabled: boolean;
  onBack: () => void;
  onSubmit: () => void;
}
function CustomForm({
  customStart, customEnd, setCustomStart, setCustomEnd,
  disabled, onBack, onSubmit,
}: CustomFormProps) {
  return (
    <>
      <SectionHeader title="Custom hours" onBack={onBack} />
      <label className="portal-label">
        <span className="portal-label-text">Start time</span>
        <input
          type="time"
          value={customStart}
          onChange={e => setCustomStart(e.target.value)}
          className="portal-input"
          required
        />
      </label>
      <label className="portal-label">
        <span className="portal-label-text">End time</span>
        <input
          type="time"
          value={customEnd}
          onChange={e => setCustomEnd(e.target.value)}
          className="portal-input"
          required
        />
        <div className="portal-hint">
          Crossing midnight is fine — e.g. 20:00 to 04:00.
        </div>
      </label>
      <div className="portal-hint" style={{ marginBottom: 12 }}>
        Custom hours need staff approval before they're confirmed.
      </div>
      <button
        type="button"
        className="portal-button"
        onClick={onSubmit}
        disabled={disabled || !customStart || !customEnd}
      >
        {disabled ? 'Submitting…' : 'Submit for approval'}
      </button>
    </>
  );
}

interface UnavailableFormProps {
  reason: string;
  setReason: (s: string) => void;
  disabled: boolean;
  onBack: () => void;
  onSubmit: () => void;
}
function UnavailableForm({ reason, setReason, disabled, onBack, onSubmit }: UnavailableFormProps) {
  return (
    <>
      <SectionHeader title="Unavailable" onBack={onBack} />
      <label className="portal-label">
        <span className="portal-label-text">Reason (optional)</span>
        <input
          type="text"
          value={reason}
          onChange={e => setReason(e.target.value)}
          className="portal-input"
          placeholder="e.g. away, family, sick"
          maxLength={120}
        />
      </label>
      <button
        type="button"
        className="portal-button"
        onClick={onSubmit}
        disabled={disabled}
      >
        {disabled ? 'Saving…' : 'Mark unavailable'}
      </button>
    </>
  );
}

interface CancelFormProps {
  cancelReason: CancelReason | null;
  setCancelReason: (r: CancelReason | null) => void;
  cancelNotes: string;
  setCancelNotes: (s: string) => void;
  disabled: boolean;
  onBack: () => void;
  onSubmit: () => void;
}
function CancelForm({
  cancelReason, setCancelReason, cancelNotes, setCancelNotes,
  disabled, onBack, onSubmit,
}: CancelFormProps) {
  const reasons: { code: CancelReason; label: string }[] = [
    { code: 'SICK', label: 'Sick' },
    { code: 'PERSONAL_EMERGENCY', label: 'Personal emergency' },
    { code: 'TRANSPORT_FAILURE', label: 'Transport issue' },
    { code: 'OTHER', label: 'Other' },
  ];
  return (
    <>
      <SectionHeader title="Cancel declaration" onBack={onBack} />
      <div className="portal-label-text" style={{ marginBottom: 8 }}>Reason</div>
      {reasons.map(r => (
        <button
          key={r.code}
          type="button"
          className={
            'portal-reason-button'
            + (cancelReason === r.code ? ' is-selected' : '')
          }
          onClick={() => setCancelReason(r.code)}
          disabled={disabled}
        >{r.label}</button>
      ))}
      {cancelReason === 'OTHER' && (
        <label className="portal-label" style={{ marginTop: 12 }}>
          <span className="portal-label-text">Tell us why</span>
          <input
            type="text"
            value={cancelNotes}
            onChange={e => setCancelNotes(e.target.value)}
            className="portal-input"
            placeholder="required"
            maxLength={200}
          />
        </label>
      )}
      <button
        type="button"
        className="portal-button portal-button-danger"
        onClick={onSubmit}
        disabled={disabled || !cancelReason}
        style={{ marginTop: 12 }}
      >
        {disabled ? 'Cancelling…' : 'Cancel this declaration'}
      </button>
    </>
  );
}

interface SectionHeaderProps {
  title: string;
  onBack: () => void;
}
function SectionHeader({ title, onBack }: SectionHeaderProps) {
  return (
    <div className="portal-section-header">
      <button type="button" onClick={onBack} className="portal-back-button" aria-label="Back">‹</button>
      <span className="portal-section-title">{title}</span>
    </div>
  );
}
